// ClaudeCode: Auth hook — manages login state and crypto key lifecycle

import { useState, useCallback, useEffect } from 'react';
import { login as apiLogin, register as apiRegister, listEntries, ssoExchange, setPassphrase as apiSetPassphrase } from '../api';
import { deriveKey, encrypt, decrypt, VERIFICATION_PLAINTEXT } from '../crypto';
import type { LoginResponse } from '../api';

// Use production Trust in prod, local Trust in dev
const TRUST_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:3002'
  : 'https://trust.lifespace.com';
const VAULT_APP_ID = '8e3b6d0a-6b25-4344-add9-f7b3d5651789';

export interface AuthState {
  user: LoginResponse['user'] | null;
  cryptoKey: CryptoKey | null;
  cryptoSalt: string | null;
  encryptedVerification: string | null; // verification blob from server
  isAuthenticated: boolean;
  isUnlocked: boolean; // has passphrase been entered this session?
  isFirstTime: boolean; // true = no passphrase set yet (no verification blob)
  ssoError: string;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    cryptoKey: null,
    cryptoSalt: null,
    encryptedVerification: null,
    isAuthenticated: false,
    isUnlocked: false,
    isFirstTime: false,
    ssoError: '',
  });

  // Handle SSO callback + restore session on mount
  useEffect(() => {
    (async () => {
      // Check for SSO callback
      const params = new URLSearchParams(window.location.search);
      const ssoCode = params.get('sso_code');
      const ssoError = params.get('sso_error');

      if (ssoError) {
        setState(prev => ({ ...prev, ssoError: decodeURIComponent(ssoError) }));
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }

      if (ssoCode) {
        window.history.replaceState({}, '', window.location.pathname);
        try {
          // Exchange code with Trust for a Trust JWT
          const trustRes = await fetch(`${TRUST_URL}/auth/exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: ssoCode }),
          });
          if (!trustRes.ok) throw new Error('Trust code exchange failed');
          const trustData = await trustRes.json();

          // Exchange Trust JWT with Vault server for a Vault session
          const vaultRes = await ssoExchange(trustData.token);

          sessionStorage.setItem('vault_token', vaultRes.token);
          sessionStorage.setItem('vault_user', JSON.stringify(vaultRes.user));
          sessionStorage.setItem('vault_crypto_salt', vaultRes.cryptoSalt);
          if (vaultRes.encryptedVerification) {
            sessionStorage.setItem('vault_encrypted_verification', vaultRes.encryptedVerification);
          }

          let firstTime = !vaultRes.hasPassphrase;
          if (firstTime) {
            try {
              const existing = await listEntries({ limit: 1 });
              if (existing.entries.length > 0) firstTime = false;
            } catch { /* leave as true */ }
          }

          setState({
            user: vaultRes.user,
            cryptoKey: null,
            cryptoSalt: vaultRes.cryptoSalt,
            encryptedVerification: vaultRes.encryptedVerification ?? null,
            isAuthenticated: true,
            isUnlocked: false,
            isFirstTime: firstTime,
            ssoError: '',
          });
          return;
        } catch (e) {
          setState(prev => ({ ...prev, ssoError: 'SSO login failed. Try again.' }));
          return;
        }
      }

      // Restore session from sessionStorage
      const token = sessionStorage.getItem('vault_token');
      const userJson = sessionStorage.getItem('vault_user');
      const salt = sessionStorage.getItem('vault_crypto_salt');
      const verification = sessionStorage.getItem('vault_encrypted_verification');

      if (token && userJson) {
        const user = JSON.parse(userJson);
        // If we have a verification blob, passphrase was set before.
        // If not, check entries to distinguish existing vs new account.
        let firstTime = !verification;
        if (firstTime) {
          try {
            const existing = await listEntries({ limit: 1 });
            if (existing.entries.length > 0) firstTime = false;
          } catch { /* leave as true */ }
        }
        setState((prev) => ({
          ...prev,
          user,
          cryptoSalt: salt,
          encryptedVerification: verification,
          isAuthenticated: true,
          isFirstTime: firstTime,
        }));
      }
    })();
  }, []);

  // Login — authenticates with server, stores token
  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      const res = await apiLogin(email, password);
      sessionStorage.setItem('vault_token', res.token);
      sessionStorage.setItem('vault_user', JSON.stringify(res.user));
      sessionStorage.setItem('vault_crypto_salt', res.cryptoSalt);
      if (res.encryptedVerification) {
        sessionStorage.setItem('vault_encrypted_verification', res.encryptedVerification);
      }

      // If hasPassphrase is set, use that. Otherwise fall back to entry check
      // (handles existing accounts that predate the verification system)
      let firstTime = !res.hasPassphrase;
      if (firstTime) {
        try {
          const existing = await listEntries({ limit: 1 });
          if (existing.entries.length > 0) firstTime = false; // has entries, not first time
        } catch { /* leave as true */ }
      }

      setState({
        user: res.user,
        cryptoKey: null,
        cryptoSalt: res.cryptoSalt,
        encryptedVerification: res.encryptedVerification ?? null,
        isAuthenticated: true,
        isUnlocked: false,
        isFirstTime: firstTime,
        ssoError: '',
      });
    },
    []
  );

  // Register — creates account, authenticates (always first time)
  const register = useCallback(
    async (
      email: string,
      password: string,
      displayName: string
    ): Promise<void> => {
      const res = await apiRegister(email, password, displayName);
      sessionStorage.setItem('vault_token', res.token);
      sessionStorage.setItem('vault_user', JSON.stringify(res.user));
      sessionStorage.setItem('vault_crypto_salt', res.cryptoSalt);

      setState({
        user: res.user,
        cryptoKey: null,
        cryptoSalt: res.cryptoSalt,
        encryptedVerification: null,
        isAuthenticated: true,
        isUnlocked: false,
        isFirstTime: true,
        ssoError: '',
      });
    },
    []
  );

  // Unlock — derives encryption key from passphrase
  // Key lives in memory only, never stored
  const unlock = useCallback(
    async (passphrase: string): Promise<void> => {
      if (!state.cryptoSalt) throw new Error('No crypto salt — login first');

      const key = await deriveKey(passphrase, state.cryptoSalt);

      if (state.encryptedVerification) {
        // Returning user — verify passphrase by decrypting the verification blob
        try {
          const result = await decrypt(state.encryptedVerification, key);
          if (result !== VERIFICATION_PLAINTEXT) {
            throw new Error('Wrong passphrase');
          }
        } catch {
          throw new Error('Wrong passphrase. Please try again.');
        }
      } else {
        // No verification blob — create one and store on server.
        // This covers both first-time users AND existing users upgrading to verification.
        const blob = await encrypt(VERIFICATION_PLAINTEXT, key);
        await apiSetPassphrase(blob);
        sessionStorage.setItem('vault_encrypted_verification', blob);
      }

      setState((prev) => ({
        ...prev,
        cryptoKey: key,
        encryptedVerification: prev.encryptedVerification || sessionStorage.getItem('vault_encrypted_verification'),
        isUnlocked: true,
        isFirstTime: false,
      }));
    },
    [state.cryptoSalt, state.encryptedVerification]
  );

  // Lock — wipes crypto key from memory
  const lock = useCallback(() => {
    setState((prev) => ({
      ...prev,
      cryptoKey: null,
      isUnlocked: false,
    }));
  }, []);

  // SSO login — redirect to Trust
  const ssoLogin = useCallback(() => {
    window.location.href = `${TRUST_URL}/auth/google?app_id=${VAULT_APP_ID}&redirect_uri=${encodeURIComponent(window.location.origin + '/')}`;
  }, []);

  // Logout — clears everything
  const logout = useCallback(() => {
    sessionStorage.removeItem('vault_token');
    sessionStorage.removeItem('vault_user');
    sessionStorage.removeItem('vault_crypto_salt');
    sessionStorage.removeItem('vault_encrypted_verification');

    setState({
      user: null,
      cryptoKey: null,
      cryptoSalt: null,
      encryptedVerification: null,
      isAuthenticated: false,
      isUnlocked: false,
      isFirstTime: false,
      ssoError: '',
    });
  }, []);

  return {
    ...state,
    login,
    register,
    ssoLogin,
    unlock,
    lock,
    logout,
  };
}
